// =============================================================================
// AdvancedRow — technikalia zwiniete: silnik, konto CF, money hosts (kompakt),
// baza kategorii, tagline, „Wykryj z WordPressa". Logika z dawnego
// MigrationSettingsCard + selektor konta z DeployStepCard.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Loader2, Wand2, RefreshCw, Settings2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCloudflareAccounts } from "@/hooks/useCloudflareAccounts";
import { detectWpSettings, type DetectedHost } from "@/services/staticSite/wpSiteDetect";
import { checkBuildWorker } from "@/services/staticSite/astroDeploy";
import { evaluateAccounts, type AccountCandidate } from "@/services/staticSite/cfAccountAllocator";
import type { BuildBackend, StaticSiteRow, StaticSiteSettings } from "@/services/staticSite/types";
import { StudioSection } from "./StudioSection";
import { MoneyHostsField, normHost } from "./MoneyHostsField";

interface Props {
  site: StaticSiteRow;
  onSaveSettings: (patch: Partial<StaticSiteSettings>) => Promise<void>;
  onPatchSite: (patch: Partial<StaticSiteRow>) => Promise<void>;
}

export function AdvancedRow({ site, onSaveSettings, onPatchSite }: Props) {
  const { toast } = useToast();
  const { accounts, loading: accountsLoading } = useCloudflareAccounts();
  const s = site.settings || { money_hosts: [] };
  const [moneyHosts, setMoneyHosts] = useState<string[]>(s.money_hosts || []);
  const [detected, setDetected] = useState<DetectedHost[]>([]);
  const [categoryBase, setCategoryBase] = useState(s.category_base ?? "category");
  const [tagline, setTagline] = useState(s.tagline || "");
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<AccountCandidate[]>([]);
  const [evaluating, setEvaluating] = useState(false);
  const [backendBusy, setBackendBusy] = useState(false);
  const autoRan = useRef(false);
  const backend: BuildBackend = s.build_backend || "browser";

  useEffect(() => {
    setMoneyHosts(site.settings?.money_hosts || []);
    setCategoryBase(site.settings?.category_base ?? "category");
    setTagline(site.settings?.tagline || "");
  }, [site.id, site.settings]);

  const dirty = useMemo(() => {
    const a = [...moneyHosts].sort().join("\n");
    const b = [...(s.money_hosts || [])].sort().join("\n");
    return a !== b || categoryBase.trim() !== (s.category_base ?? "category") || tagline.trim() !== (s.tagline || "");
  }, [moneyHosts, categoryBase, tagline, s.money_hosts, s.category_base, s.tagline]);

  const detect = useCallback(async (auto: boolean) => {
    setDetecting(true);
    try {
      const res = await detectWpSettings(site.map_id, site.website_id);
      setDetected(res.hosts);
      setWarnings(res.warnings);
      const nextHosts = res.hosts.map((h) => h.host);
      const nextBase = res.categoryBase ?? (site.settings?.category_base ?? "category");
      const nextTagline = res.tagline || site.settings?.tagline || "";
      setMoneyHosts(nextHosts);
      setCategoryBase(nextBase);
      setTagline(nextTagline);
      if (auto) await onSaveSettings({ money_hosts: nextHosts, category_base: nextBase, tagline: nextTagline || undefined, detected_at: new Date().toISOString() });
      if (!auto) toast({ title: "Wykryto ponownie", description: `${res.hosts.length} hostów zewnętrznych` });
    } catch (e) {
      toast({ title: "Wykrywanie nieudane", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setDetecting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.map_id, site.website_id, site.settings, onSaveSettings]);

  // Pierwsze otwarcie migracji: wykryj i zapisz automatycznie (jak dotad)
  useEffect(() => {
    if (autoRan.current || site.settings?.detected_at) return;
    autoRan.current = true;
    void detect(true);
  }, [site.id, site.settings?.detected_at, detect]);

  // Lista hostow do chipow — gdy nie wykrywano w tej sesji, pokaz zapisane
  useEffect(() => {
    if (!detected.length && site.settings?.detected_at) {
      void detectWpSettings(site.map_id, site.website_id).then((r) => setDetected(r.hosts)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id]);

  const save = async () => {
    setSaving(true);
    try {
      await onSaveSettings({
        money_hosts: [...new Set(moneyHosts.map(normHost).filter(Boolean))],
        category_base: categoryBase.trim().replace(/^\/+|\/+$/g, ""),
        tagline: tagline.trim() || undefined,
        detected_at: site.settings?.detected_at || new Date().toISOString(),
      });
      toast({ title: "Zapisano ustawienia" });
    } catch (e) {
      toast({ title: "Nie udało się zapisać", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const setBackend = async (next: BuildBackend) => {
    if (next === backend) return;
    setBackendBusy(true);
    try {
      if (next === "astro") {
        const check = await checkBuildWorker();
        if (!check.ok) { toast({ title: "Build-worker niedostępny", description: check.message, variant: "destructive" }); return; }
      }
      await onSaveSettings({ build_backend: next, astro_theme: next === "astro" ? (s.astro_theme || "brand") : undefined });
    } finally {
      setBackendBusy(false);
    }
  };

  const evaluate = async () => {
    if (!accounts.length) return;
    setEvaluating(true);
    try { setCandidates(await evaluateAccounts(accounts, site.domain)); } finally { setEvaluating(false); }
  };

  const accountLabel = accounts.find((a) => a.id === site.pages_cf_account_id)?.label || "auto (allocator)";
  const summary = (
    <span className="text-xs text-muted-foreground">
      silnik: {backend === "astro" ? "Astro" : "własny renderer"} · konto CF: {accountLabel} · money hosts: {(s.money_hosts || []).length} · kategorie: /{s.category_base ?? "category"}/ {s.tagline ? `· „${s.tagline.slice(0, 40)}”` : ""}
    </span>
  );

  return (
    <StudioSection
      icon={Settings2}
      title="Zaawansowane"
      collapsible
      defaultOpen={false}
      summary={summary}
      footer={
        <>
          {site.settings?.detected_at && !dirty && <span className="text-xs text-muted-foreground">zapisane · wykryto {new Date(site.settings.detected_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}</span>}
          <Button size="sm" onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Zapisz
          </Button>
        </>
      }
      help="Silnik budowania, konto Cloudflare, hosty linków sprzedażowych, baza adresów kategorii i tagline. Wykrywane z WordPressa przy pierwszym otwarciu.">
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Silnik budowania</Label>
            <div className="flex gap-2">
              {(["browser", "astro"] as BuildBackend[]).map((b) => (
                <Button key={b} size="sm" variant={backend === b ? "secondary" : "outline"} onClick={() => void setBackend(b)} disabled={backendBusy}>
                  {backendBusy && backend !== b ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                  {b === "browser" ? "Własny renderer (przeglądarka)" : "Astro (build-worker)"}
                </Button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">To samo, co karta motywu wyżej. Astro buduje w kontenerze na Coolify (ok. 30–90 s); własny renderer w kilka sekund w przeglądarce.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Konto Cloudflare dla projektu <span className="font-mono">{site.pages_project_name || "(nowy)"}</span></Label>
            <div className="flex gap-2">
              <Select value={site.pages_cf_account_id || "auto"} onValueChange={(v) => void onPatchSite({ pages_cf_account_id: v === "auto" ? null : v })} disabled={accountsLoading}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto" className="text-xs">auto — konto z zone domeny albo najmniej projektów</SelectItem>
                  {accounts.map((a) => {
                    const c = candidates.find((x) => x.account.id === a.id);
                    return <SelectItem key={a.id} value={a.id} className="text-xs">{a.label}{c ? ` · ${c.projectCount}/100${c.ownsZone ? " · ma zone" : ""}${c.error ? ` · ${c.error.slice(0, 40)}` : ""}` : ""}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="h-8" onClick={() => void evaluate()} disabled={evaluating || !accounts.length} title="Sprawdź liczbę projektów">
                {evaluating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cat-base" className="text-xs">Baza adresów kategorii</Label>
              <Input id="cat-base" value={categoryBase} onChange={(e) => setCategoryBase(e.target.value)} placeholder="category" className="h-8 font-mono text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tagline" className="text-xs">Tagline strony</Label>
              <Input id="tagline" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="Z WordPressa (Opis witryny)" className="h-8 text-xs" />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Hosty linków sprzedażowych (chronione bajt w bajt)</Label>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => void detect(false)} disabled={detecting}>
              {detecting ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />} Wykryj z WordPressa
            </Button>
          </div>
          <MoneyHostsField detected={detected} value={moneyHosts} onChange={setMoneyHosts} />
          {warnings.length > 0 && <ul className="space-y-0.5 text-[11px] text-amber-700 dark:text-amber-400">{warnings.map((w, i) => <li key={i}>• {w}</li>)}</ul>}
        </div>
      </div>
    </StudioSection>
  );
}
